import React, {useEffect, useRef} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {FlexRow, Heading, Icon} from '@forecast-it/design-system';
import {LabelledIconButton} from '../labelled-icon-button/LabelledIconButton';
import {useRightDrawerOffset} from '../../hooks/useRightDrawerOffset';

export const RIGHT_DRAWER_WIDTH = 480;

// Fixed to the window from the top of the top navigation to the bottom, over the page, the same width as
// the Ask AI drawer. translateX alone would leave a closed drawer's contents focusable, so visibility
// hides it too, delayed until the slide out has finished.
const Frame = styled.div<{isOpen: boolean}>`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: ${RIGHT_DRAWER_WIDTH}px;
	transform: translateX(${({isOpen}) => (isOpen ? '0' : `${RIGHT_DRAWER_WIDTH}px`)});
	transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear ${({isOpen}) => (isOpen ? '0s' : '250ms')};
	visibility: ${({isOpen}) => (isOpen ? 'visible' : 'hidden')};
	z-index: 100;
	display: flex;
	flex-direction: column;
	background: ${({theme}) => theme.sys.color.background.neutral['1']};
	border-left: 1px solid ${({theme}) => theme.sys.color.border.decorative};
	box-shadow: ${({theme}) => theme.sys.elevation.shadow['2']};

	&:focus {
		outline: none;
	}
`;

const Header = styled.div`
	flex: none;
	padding: 14px 18px;
`;

const Body = styled.div`
	flex: 1;
	min-height: 0;
	overflow: auto;
`;

type IconName = React.ComponentProps<typeof Icon>['icon'];

type RightDrawerProps = {
	isOpen: boolean;
	title: string;
	icon?: IconName;
	closeLabel: string;
	onClose: () => void;
	children: React.ReactNode;
	'data-cy'?: string;
};

export const RightDrawer = ({isOpen, title, icon, closeLabel, onClose, children, ...rest}: RightDrawerProps): JSX.Element => {
	useRightDrawerOffset(isOpen, RIGHT_DRAWER_WIDTH);
	// Callers pass onClose inline, so it is read through a ref and the effects key on isOpen alone.
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	const frameRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			// An open menu inside the drawer claims Escape for itself; only an unclaimed Escape closes the drawer.
			if (event.key === 'Escape' && !event.defaultPrevented) {
				onCloseRef.current();
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen]);

	// Focus moves into the drawer when it opens, so a keyboard or screen reader user starts there, and goes
	// back to whatever opened it when it closes. The drawer sits beside the page rather than over it, like
	// Ask AI, so focus is not trapped: Tab can still leave it.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const returnFocusTo = document.activeElement as HTMLElement | null;
		frameRef.current?.focus();
		return () => returnFocusTo?.focus?.();
	}, [isOpen]);

	// Portalled to the body so no ancestor (such as the header bar) can contain the fixed frame or cap its stacking.
	return ReactDOM.createPortal(
		<Frame ref={frameRef} tabIndex={-1} isOpen={isOpen} role={'dialog'} aria-label={title} data-cy={rest['data-cy']}>
			<Header>
				<FlexRow alignItems={'center'} gap={'s'}>
					{icon ? <Icon icon={icon} size={'m'} /> : null}
					<Heading size={'4'}>{title}</Heading>
					<FlexRow flex={'1 1 auto'} />
					<LabelledIconButton icon={'close'} label={closeLabel} onPress={onClose} />
				</FlexRow>
			</Header>
			<Body>{isOpen ? children : null}</Body>
		</Frame>,
		document.body
	);
};
